import { KatFileUpload } from '@amzn/katal-react';
import React, { FC } from 'react';
import { StyledRadioButtonGroup } from 'src/components/general/StyledRadioButtonGroup';
import { ConfirmationDialog } from 'src/components/general/ConfirmationDialog';
import { StatusIndicator } from 'src/components/general/StatusIndicator';
import { useAssetInput } from 'src/hooks/assetInput/useAssetInput';
import { getFileUploadState } from 'src/utils/assetInput/assetInputUtils';
import { InputType, AssetStatus } from 'src/types';
import { UrlInput } from './UrlInput';
import { AssetInputProps, INPUT_OPTIONS } from '.';
import styles from './AssetInput.module.css';

export const AssetInput: FC<AssetInputProps> = ({
  label,
  assetInputHandler,
  acceptedFileTypes,
  maxFileSizeInBytes,
  error,
  status,
}) => {
  const {
    selectedType,
    url,
    showConfirmDialog,
    radioGroupName,
    isValidUrlInput,
    handleUrlChange,
    handleUrlFetch,
    handleFileAttached,
    handleFileReplaced,
    handleFileAttachedError,
    handleRadioChange,
    handleConfirmTypeChange,
    handleCancelTypeChange,
    getConfirmationMessage,
  } = useAssetInput({
    label,
    assetInputHandler,
    status,
  });

  const fileUploadStatus = getFileUploadState(status, error);

  return (
    <div className={styles.container} data-testid="asset-input">
      <div className={styles.radioGroup}>
        <StyledRadioButtonGroup
          name={radioGroupName}
          options={INPUT_OPTIONS}
          value={selectedType}
          onChange={handleRadioChange}
          label={label}
          data-testid="input-type-selector"
        />
      </div>

      {selectedType === InputType.URL ? (
        <UrlInput
          url={url}
          onChange={handleUrlChange}
          onFetch={handleUrlFetch}
          isValidUrl={isValidUrlInput}
          isFetching={status === AssetStatus.PENDING_UPLOAD}
        />
      ) : (
        <KatFileUpload
          accept={acceptedFileTypes}
          maxSize={maxFileSizeInBytes}
          onFilesAttached={handleFileAttached}
          onFilesRemoved={assetInputHandler.handleFileRemoved}
          onFilesReplaced={handleFileReplaced}
          onFilesAttachedError={handleFileAttachedError}
          hideClearAll
          state={fileUploadStatus}
          data-testid="file-upload"
        />
      )}

      <ConfirmationDialog
        isVisible={showConfirmDialog}
        title="Change Input"
        message={getConfirmationMessage()}
        onConfirm={handleConfirmTypeChange}
        onCancel={handleCancelTypeChange}
        data-testid="asset-input-confirm-dialog"
      />

      <StatusIndicator
        error={error}
        isLoading={status === AssetStatus.PENDING_UPLOAD}
        isSuccess={status === AssetStatus.COMPLETED}
        data-testid="asset-input-status"
      />
    </div>
  );
};
